import * as React from "react";
import { createBrowserRouter } from "react-router-dom";

// import pages
import App from "../pages/App/App";
import Home from "../pages/App/Home/Home";
import Campaigns from "../pages/App/Campaigns/Campaigns";
import Contacts from "../pages/App/Contacts/Contacts";
import Compliance from "../pages/App/Compliance/Compliance";
import Settings from "../pages/App/Settings/Settings";
import NotFound from "../pages/App/NotFound/NotFound";

import Auth from "../pages/Auth/Auth";
import Signup from "../pages/Auth/Signup";
import LandingPage from "../pages/Public/LandingPage";
import AboutUs from "../pages/Public/AboutUs";
import Pricing from "../pages/Public/Pricing";
import Community from "../pages/Public/Community";
import Unsubscribe from "../pages/Public/Unsubscribe";
import FormThankYou from "../pages/Public/FormThankYou";
import HostedForm from "../pages/Public/HostedForm";
import TrackRedirect from "../pages/Public/TrackRedirect";
import QuickCampaignCreator from "../pages/App/QuickCampaignCreator/QuickCampaignCreator";
import TemplatesList from "../pages/App/Templates/TemplatesList";
import FormsList from "../pages/App/Forms/FormsList";
import FormDetail from "../pages/App/Forms/FormDetail";
import FormIndexRoute from "../pages/App/Forms/FormIndexRoute";
import FormSubmissionsPanel from "../pages/App/Forms/FormSubmissionsPanel";
import FormSettings from "../pages/App/Forms/FormSettings";
import FormFields from "../pages/App/Forms/FormFields";
import FormDpdp from "../pages/App/Forms/FormDpdp";
import FormLeadScoring from "../pages/App/Forms/FormLeadScoring";
import FormIntegration from "../pages/App/Forms/FormIntegration";
import InviteAccept from "../pages/App/InviteAccept/InviteAccept";
import RootLayout from "../layouts/RootLayout";
import AdminApp from "../pages/Admin/AdminApp";
import AdminLogin from "../pages/Admin/AdminLogin";
import AdminDashboard from "../pages/Admin/AdminDashboard";
import AdminOrganizations from "../pages/Admin/AdminOrganizations";
import AdminOrganizationDetail from "../pages/Admin/AdminOrganizationDetail";
import AdminUserDetail from "../pages/Admin/AdminUserDetail";
import AdminFormLogs from "../pages/Admin/AdminFormLogs";
import AdminEmailLogs from "../pages/Admin/AdminEmailLogs";
import AdminUserLogs from "../pages/Admin/AdminUserLogs";
import AdminActivityLog from "../pages/Admin/AdminActivityLog";
import SequencesList from "../pages/App/Sequences/SequencesList";
import SequenceDetail from "../pages/App/Sequences/SequenceDetail";
import SequenceEditor from "../pages/App/Sequences/SequenceEditor";
import SequenceEnrollments from "../pages/App/Sequences/SequenceEnrollments";
import SequenceReport from "../pages/App/Sequences/SequenceReport";

const appRoutes = {
  path: "/app",
  element: <App />,
  children: [
    {
      path: "",
      element: <Home />,
    },
    {
      path: "campaigns",
      element: <Campaigns />,
    },
    {
      path: "editor",
      element: <QuickCampaignCreator />,
    },
    {
      path: "templates",
      element: <TemplatesList />,
    },
    {
      path: "contacts",
      element: <Contacts />,
    },
    {
      path: "sequences",
      element: <SequencesList />,
    },
    {
      path: "sequences/:id",
      element: <SequenceDetail />,
      children: [
        { index: true, element: <SequenceEditor /> },
        { path: "enrollments", element: <SequenceEnrollments /> },
        { path: "report", element: <SequenceReport /> },
      ],
    },
    {
      path: "compliance",
      element: <Compliance />,
    },
    {
      path: "forms",
      element: <FormsList />,
    },
    {
      path: "forms/:id",
      element: <FormDetail />,
      children: [
        { index: true, element: <FormIndexRoute /> },
        { path: "submissions", element: <FormSubmissionsPanel /> },
        { path: "settings", element: <FormSettings /> },
        { path: "fields", element: <FormFields /> },
        { path: "dpdp", element: <FormDpdp /> },
        { path: "lead-scoring", element: <FormLeadScoring /> },
        { path: "integration", element: <FormIntegration /> },
      ],
    },
    {
      path: "settings",
      element: <Settings />,
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
};

const authRoutes = {
  path: "/",
  children: [
    {
      path: "",
      element: <LandingPage />,
    },
    {
      path: "about",
      element: <AboutUs />,
    },
    {
      path: "pricing",
      element: <Pricing />,
    },
    {
      path: "community",
      element: <Community />,
    },
    {
      path: "login",
      element: <Auth />,
    },
    {
      path: "signup",
      element: <Signup />,
    },
    {
      path: "unsubscribe/:emailAddress/:organizationName/:campaignId?",
      element: <Unsubscribe />,
    },
    {
      path: "f/:publicId",
      element: <HostedForm />,
    },
    {
      path: "forms/thank-you",
      element: <FormThankYou />,
    },
    {
      path: "invite/:token",
      element: <InviteAccept />,
    },
    {
      path: "mailinflow/track",
      element: <TrackRedirect />,
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
};

// Admin portal: fully separate session (adminAccessToken, not accessToken),
// so its route group is gated independently from the regular app/auth split
// above. /admin/login is always reachable regardless of admin session state.
const adminLoginRoute = {
  path: "/admin/login",
  element: <AdminLogin />,
};

const adminRoutes = {
  path: "/admin",
  element: <AdminApp />,
  children: [
    { path: "", element: <AdminDashboard /> },
    { path: "organizations", element: <AdminOrganizations /> },
    { path: "organizations/:id", element: <AdminOrganizationDetail /> },
    { path: "users/:id", element: <AdminUserDetail /> },
    { path: "form-logs", element: <AdminFormLogs /> },
    { path: "email-logs", element: <AdminEmailLogs /> },
    { path: "user-logs", element: <AdminUserLogs /> },
    { path: "activity-log", element: <AdminActivityLog /> },
  ],
};

// create router — invite route is always available (logged in or out)
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      ...(localStorage.getItem("adminAccessToken")
        ? [adminRoutes]
        : []),
      adminLoginRoute,
      ...(localStorage.getItem("accessToken")
        ? [appRoutes, authRoutes]
        : [authRoutes]),
    ],
  },
]);

export default router;
